import React from "react";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  Heading,
  AccordionPanel,
  HStack,
} from "@chakra-ui/react";

import { ModuleGrid } from "@components";
import { Module, ModuleWithUpNext, getHumanReadableStepName } from "@constants";
import { getNextStepsForModules } from "@utils";

type UpNextGridStackProps = {
  modulesUpNext: ModuleWithUpNext[];
  recentModules: Module[];
};

type ModulesByStep = [string, ModuleWithUpNext[]];

/**
 * Intermediary component to handle grouping of modules by the step they have
 * next to perform.
 * @param {Modules[]} modulesUpNext the fetched modules for the location.
 */
export function UpNextGridStack({
  modulesUpNext,
  recentModules,
}: UpNextGridStackProps) {
  const modulesUpNextByStep = React.useMemo<ModulesByStep[]>(() => {
    return Array.from(
      new Set(modulesUpNext.map((moduleUpNext) => moduleUpNext.nextStep)),
    ).map((stepName) => [
      stepName,
      modulesUpNext.filter(
        (moduleUpNext) => moduleUpNext.nextStep === stepName,
      ),
    ]);
  }, [modulesUpNext]);

  const displayRecent = recentModules.length >= 1;

  return (
    <Accordion
      w="100%"
      defaultIndex={displayRecent ? [0] : undefined}
      allowMultiple
      allowToggle
    >
      {recentModules.length >= 1 ? (
        <AccordionItem w="100%">
          <AccordionButton as={HStack}>
            <Heading as="h2" size="md" flex={1}>
              Pick up where you left off!
            </Heading>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel>
            <ModuleGrid modules={getNextStepsForModules(recentModules)} />
          </AccordionPanel>
        </AccordionItem>
      ) : undefined}
      {modulesUpNextByStep
        .sort(([stepNameA], [stepNameB]) => stepNameA.localeCompare(stepNameB))
        .map(([stepName, modulesAtStep]) => (
          <AccordionItem key={stepName}>
            <AccordionButton as={HStack}>
              <Heading as="h2" size="md" flex={1}>
                {getHumanReadableStepName(stepName)} ({modulesAtStep.length})
              </Heading>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel>
              <ModuleGrid modules={modulesAtStep} />
            </AccordionPanel>
          </AccordionItem>
        ))}
    </Accordion>
  );
}
